import { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { AdminLayout } from './components/admin/AdminLayout';
import { ScrollToTop } from './components/common/ScrollToTop';
import { HomePage } from './pages/HomePage';
import { SearchPage } from './pages/SearchPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { PasswordResetRequestPage } from './pages/PasswordResetRequestPage';
import { PasswordResetPage } from './pages/PasswordResetPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ConfirmEmailChangePage } from './pages/ConfirmEmailChangePage';
import { ContactPage } from './pages/ContactPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { AboutPage } from './pages/AboutPage';
import { AccountPage } from './pages/AccountPage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { PurchaseHistoryPage } from './pages/PurchaseHistoryPage';
import { DictionariesPage } from './pages/DictionariesPage';
import { AdminPage } from './pages/AdminPage';
import { LogsPage } from './pages/admin/LogsPage';
import { UsersManagementPage } from './pages/admin/UsersManagementPage';
import { UserDetailPage } from './pages/admin/UserDetailPage';
import { DictionariesManagementPage } from './pages/admin/DictionariesManagementPage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { LegalPagesPage } from './pages/admin/LegalPagesPage';
import { StatsPage } from './pages/admin/StatsPage';
import { FinancesPage } from './pages/admin/FinancesPage';
import { MaintenancePage } from './pages/MaintenancePage';
import { NotFoundPage } from './pages/NotFoundPage';
import { useStore } from './store';

/**
 * Spellion App - Main Application Component
 * 
 * Configures React Router routes for the SPA:
 * - / : Home page with word search functionality
 * - /search : Dedicated search page
 * - /login : User login
 * - /register : New user registration
 * - /password-reset-request : Password recovery request form
 * - /password-reset : Password reset form (with token from email)
 * - /account : User account management (protected)
 * - /subscription : Subscription management page (protected)
 * - /dictionaries : Custom dictionaries management (premium only)
 * - /admin/* : Administration routes (admin only, uses AdminLayout)
 * 
 * All routes are wrapped in the Layout component for
 * consistent header, navigation, and footer.
 * 
 * Language initialization:
 * - Calls initializeLanguage() on mount to detect/restore user's language preference
 * - Non-blocking initialization per Requirement 9.1
 * 
 * Maintenance mode:
 * - Loads maintenance status from /api/config/public
 * - Shows MaintenancePage for non-admin users when maintenance is enabled
 * - Admin users can still access /admin routes during maintenance
 * 
 * @validates Requirements 9.1, 9.2
 */
function App() {
  const location = useLocation();
  
  // Use direct selector for the initializeLanguage action
  const initializeLanguage = useStore((state) => state.initializeLanguage);
  const loadPublicConfig = useStore((state) => state.loadPublicConfig);
  const maintenanceMode = useStore((state) => state.maintenanceMode);
  const configLoaded = useStore((state) => state.configLoaded);
  const isAdmin = useStore((state) => state.isAdmin);

  // Initialize language on app startup
  // Per Requirement 9.1: Language detection begins on initialization
  // Content MAY display during determination (non-blocking)
  useEffect(() => {
    initializeLanguage();
    loadPublicConfig();
  }, [initializeLanguage, loadPublicConfig]);

  // Check if we should show maintenance page
  // Admin users and admin routes bypass maintenance
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLoginRoute = location.pathname === '/login';
  const showMaintenance = configLoaded && maintenanceMode && !isAdminRoute && !isLoginRoute && !isAdmin();

  // Show maintenance page for non-admin users when maintenance is enabled
  if (showMaintenance) {
    return <MaintenancePage />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
      <Route path="/" element={<Layout />}>
        {/* Public routes */}
        <Route index element={<HomePage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="verify-email" element={<VerifyEmailPage />} />
        <Route path="confirm-email-change/:token" element={<ConfirmEmailChangePage />} />
        <Route path="password-reset-request" element={<PasswordResetRequestPage />} />
        <Route path="password-reset" element={<PasswordResetPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="privacy" element={<PrivacyPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route path="about" element={<AboutPage />} />
        
        {/* Protected routes */}
        <Route path="account" element={<AccountPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="purchase-history" element={<PurchaseHistoryPage />} />
        <Route path="dictionaries" element={<DictionariesPage />} />
        
        {/* 404 fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Admin routes with AdminLayout */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminPage />} />
        <Route path="logs" element={<LogsPage />} />
        <Route path="users" element={<UsersManagementPage />} />
        <Route path="users/:userId" element={<UserDetailPage />} />
        <Route path="dictionaries" element={<DictionariesManagementPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="legal-pages" element={<LegalPagesPage />} />
        <Route path="stats" element={<StatsPage />} />
        <Route path="finances" element={<FinancesPage />} />
      </Route>
    </Routes>
    </>
  );
}

export default App;
